import { current } from '../helpers'
import {
  cardTransferingCardTablesPath,
  cardTransferingCardTableColumnsPath,
  cardTransferingColumnCardsPath,
  cardTransferingTargetCardTableTargetColumnsPath
} from '../routes'
import SelectAllController from './select_all_controller'

const CARD_TABLES_FRAME_ID = 'card_transfering_card_tables'
const COLUMNS_FRAME_ID = 'card_transfering_columns'
const CARDS_FRAME_ID = 'card_transfering_cards'
const TARGET_COLUMNS_FRAME_ID = 'target_card_transfering_columns'

const TARGET_COLUMN_INPUT_SELECTOR = 'card_transfering[target_column_id]'

export default class extends SelectAllController {
  static targets = [
    'projectSelect', 'submitButton', 'buttonCount', 'scrollHint',
    'projectStep', 'cardTableStep', 'columnStep',
    'cardsStep', 'targetTableStep', 'targetColumnStep'
  ]

  connect () {
    const ctFrame = document.getElementById(CARD_TABLES_FRAME_ID)
    if (ctFrame) this.initialCardTablesHTML = ctFrame.innerHTML
    const colFrame = document.getElementById(COLUMNS_FRAME_ID)
    if (colFrame) this.initialColumnsHTML = colFrame.innerHTML
    const cardsFrame = document.getElementById(CARDS_FRAME_ID)
    if (cardsFrame) this.initialCardsHTML = cardsFrame.innerHTML

    this.onScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll, { passive: true })
    document.addEventListener('turbo:frame-load', this.onScroll)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onScroll)
      this.resizeObserver.observe(document.body)
    }
    this.onScroll()
  }

  disconnect () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
    document.removeEventListener('turbo:frame-load', this.onScroll)
    this.resizeObserver?.disconnect()
  }

  onScroll () {
    if (!this.hasScrollHintTarget) return
    const docHeight = document.documentElement.scrollHeight
    const viewport = window.innerHeight
    const scrolled = window.scrollY
    const distanceFromBottom = docHeight - (scrolled + viewport)
    const isScrollable = docHeight > viewport + 100
    const shouldShow = isScrollable && distanceFromBottom > 200
    this.scrollHintTarget.style.opacity = shouldShow ? '1' : '0'
  }

  selectProject () {
    this.resetSubmitButton()
    this.resetCards()
    this.resetColumns()
    this.setStepState(this.columnStepTarget, 'disabled')
    if (this.projectId) {
      this.loadCardTables()
      this.setStepState(this.projectStepTarget, 'done')
      this.setStepState(this.cardTableStepTarget, 'active')
    } else {
      this.resetCardTables()
      this.setStepState(this.projectStepTarget, 'active')
      this.setStepState(this.cardTableStepTarget, 'disabled')
    }
  }

  selectCardTable ({ target }) {
    this.resetSubmitButton()
    this.resetCards()
    this.loadColumns(target.value)
    this.setStepState(this.cardTableStepTarget, 'done')
    this.setStepState(this.columnStepTarget, 'active')
  }

  selectColumn ({ target }) {
    this.resetSubmitButton()
    this.setStepState(this.columnStepTarget, 'done')
    this.loadCards(target.value)
  }

  selectTargetCardTable ({ target }) {
    this.loadTargetColumns(target.value)
    if (this.hasTargetTableStepTarget) this.setStepState(this.targetTableStepTarget, 'done')
    if (this.hasTargetColumnStepTarget) this.setStepState(this.targetColumnStepTarget, 'active')
    this.submitButtonVisible = true
    this.submitButtonDisabled = true
  }

  selectTargetColumn () {
    this.updateSubmitButtonState()
  }

  toggle (e) {
    super.toggle(e)
    this.updateSubmitButtonState()
  }

  refresh () {
    super.refresh()
    this.updateSubmitButtonState()
  }

  loadCardTables () {
    const frame = document.getElementById(CARD_TABLES_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.cardsGridSkeleton())
    frame.src = cardTransferingCardTablesPath(current.account.id, this.projectId)
  }

  loadColumns (cardTableId) {
    const frame = document.getElementById(COLUMNS_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.chipsSkeleton())
    frame.src = cardTransferingCardTableColumnsPath(current.account.id, this.projectId, cardTableId)
  }

  loadCards (columnId) {
    const frame = document.getElementById(CARDS_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.listSkeleton())
    frame.src = cardTransferingColumnCardsPath(current.account.id, this.projectId, columnId)
  }

  loadTargetColumns (cardTableId) {
    const frame = document.getElementById(TARGET_COLUMNS_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.chipsSkeleton())
    frame.src = cardTransferingTargetCardTableTargetColumnsPath(current.account.id, this.projectId, cardTableId)
  }

  resetCardTables () {
    const frame = document.getElementById(CARD_TABLES_FRAME_ID)
    if (!frame || !this.initialCardTablesHTML) return
    frame.removeAttribute('src')
    frame.innerHTML = this.initialCardTablesHTML
  }

  resetColumns () {
    const frame = document.getElementById(COLUMNS_FRAME_ID)
    if (!frame || !this.initialColumnsHTML) return
    frame.removeAttribute('src')
    frame.innerHTML = this.initialColumnsHTML
  }

  resetCards () {
    const frame = document.getElementById(CARDS_FRAME_ID)
    if (!frame || !this.initialCardsHTML) return
    frame.removeAttribute('src')
    frame.innerHTML = this.initialCardsHTML
  }

  updateSubmitButtonState () {
    const count = this.checked.length
    this.submitButtonDisabled = count === 0 || !this.targetColumnSelected
    this.buttonCountContent = count
  }

  // eslint-disable-next-line accessor-pairs
  set buttonCountContent (count) {
    if (!this.hasButtonCountTarget) return
    this.buttonCountTarget.textContent = count === 1 ? '1 card' : `${count} cards`
  }

  get targetColumnSelected () {
    return !!this.element.querySelector(`input[name="${TARGET_COLUMN_INPUT_SELECTOR}"]:checked`)
  }
}
